package com.evoapps.evoshift.data.repository

import com.evoapps.evoshift.data.remote.CloudFunctionsClient
import com.evoapps.evoshift.data.remote.CloudFunctionsException
import com.evoapps.evoshift.domain.model.ChecklistCheck
import com.evoapps.evoshift.domain.model.ChecklistKind
import com.evoapps.evoshift.domain.model.KioskOfflineException
import com.evoapps.evoshift.domain.model.ShiftChecklist
import com.evoapps.evoshift.domain.repository.KioskChecklistMoment
import com.evoapps.evoshift.domain.repository.KioskChecklistRepository
import kotlinx.coroutines.CancellationException
import kotlinx.serialization.Serializable
import kotlinx.serialization.json.Json
import kotlinx.serialization.json.JsonObject
import kotlinx.serialization.json.add
import kotlinx.serialization.json.buildJsonObject
import kotlinx.serialization.json.put
import kotlinx.serialization.json.putJsonArray

/**
 * SH-127 over the kioskChecklist function (functions/kioskChecklist.js): the tablet's identity
 * (its ID token) plus the person's PIN, checked by the server on every call.
 */
class KioskChecklistRepositoryImpl(private val functions: CloudFunctionsClient) : KioskChecklistRepository {
    private val json = Json { ignoreUnknownKeys = true }

    override suspend fun list(userId: String, pin: String, shiftId: String, moment: KioskChecklistMoment): Result<List<ShiftChecklist>> =
        call(userId, pin, shiftId, "LIST") { put("moment", moment.name) }.map { result ->
            val listed = json.decodeFromJsonElement(KioskChecklistsDto.serializer(), result)
            listed.checklists.map { it.toDomain(shiftId, userId) }
        }

    override suspend fun tick(
        userId: String,
        pin: String,
        shiftId: String,
        templateId: String,
        itemId: String,
        done: Boolean,
        note: String?
    ): Result<ShiftChecklist> = call(userId, pin, shiftId, "TICK") {
        put("templateId", templateId)
        put("itemId", itemId)
        put("done", done)
        note?.let { put("note", it) }
    }.map { result -> json.decodeFromJsonElement(KioskTickDto.serializer(), result).checklist.toDomain(shiftId, userId) }

    override suspend fun skip(userId: String, pin: String, shiftId: String, templateIds: List<String>): Result<Unit> =
        call(userId, pin, shiftId, "SKIP") { putJsonArray("templateIds") { templateIds.forEach { add(it) } } }.map { }

    private suspend fun call(
        userId: String,
        pin: String,
        shiftId: String,
        op: String,
        extra: kotlinx.serialization.json.JsonObjectBuilder.() -> Unit
    ): Result<JsonObject> = try {
        val result = functions.call("kioskChecklist", buildJsonObject {
            put("userId", userId)
            put("pin", pin)
            put("shiftId", shiftId)
            put("op", op)
            extra()
        })
        Result.success(result as? JsonObject ?: JsonObject(emptyMap()))
    } catch (e: CancellationException) {
        throw e
    } catch (e: CloudFunctionsException) {
        Result.failure(KioskRepositoryImpl.refusalOf(e))
    } catch (e: Exception) {
        // Ktor's connection and timeout failures: the request never reached the function.
        Result.failure(KioskOfflineException(e))
    }
}

@Serializable
private data class KioskChecklistsDto(val shiftId: String = "", val checklists: List<KioskChecklistDto> = emptyList())

@Serializable
private data class KioskTickDto(val checklist: KioskChecklistDto)

/** A checklist as kioskChecklist returns it (the shift's fields are the caller's). */
@Serializable
private data class KioskChecklistDto(
    val templateId: String,
    val name: String = "",
    val kind: String = "CUSTOM",
    val items: List<ChecklistItemDto> = emptyList(),
    val checks: Map<String, ChecklistCheckDto> = emptyMap(),
    val completedAt: String? = null,
    val skippedAt: String? = null
) {
    fun toDomain(shiftId: String, userId: String) = ShiftChecklist(
        shiftId = shiftId,
        templateId = templateId,
        // The tablet never stores it: the workplace, roster and day are the server's business.
        workplaceId = "",
        rosterId = "",
        date = "",
        userId = userId,
        name = name,
        kind = ChecklistKind.fromName(kind),
        items = items.map { it.toDomain() },
        checks = checks.mapValues { (_, c) -> ChecklistCheck(c.done, c.note, c.at) },
        completedAt = completedAt,
        skippedAt = skippedAt
    )
}
